import { useMemo, useState } from 'react';

// Drafts belong to the signed-in account, never to the token (which can rotate).
export default function useStockDrafts(userId, products) {
  const key = userId ? `stock-drafts:v1:${userId}` : null;
  const [stored, setStored] = useState({});
  const drafts = useMemo(() => {
    if (!key) return {};
    if (stored.key === key) return stored.drafts;
    try {
      const value = JSON.parse(localStorage.getItem(key) || '{}');
      return value && !Array.isArray(value) && typeof value === 'object' ? value : {};
    } catch { return {}; }
  }, [key, stored]);

  const save = (next) => {
    if (!key) throw new Error('Sign in before saving stock drafts.');
    // Persist before acknowledging the edit; storage failure must not lose work silently.
    localStorage.setItem(key, JSON.stringify(next));
    setStored({ key, drafts: next });
  };
  const stageMany = (entries) => {
    const next = { ...drafts };
    for (const { id, changes } of entries) {
    const original = products.find(row => String(row.id) === String(id));
    if (!original) throw new Error('Stock item is no longer available. Refresh before editing.');
    const previous = next[id];
    if (!previous && Object.keys(next).length >= 500) throw new Error('Publish the current 500 drafts before adding more items.');
    next[id] = {
      inventoryProductId: id,
      name: original.product_name || original.sku_id || String(id),
      changes: { ...previous?.changes, ...changes,
        financial_changed: Boolean(previous?.changes.financial_changed || changes.financial_changed),
        cascade_dispatch_financials: Boolean(changes.financial_changed ? changes.cascade_dispatch_financials : previous?.changes.cascade_dispatch_financials),
        sync_pos_mongo: Boolean(changes.financial_changed ? changes.sync_pos_mongo : previous?.changes.sync_pos_mongo),
      },
      expected_stock: previous ? previous.expected_stock : original.count_in_stock,
      expected_updated_at: previous ? previous.expected_updated_at : original.updated_at,
    };
    }
    save(next);
    return { staged: true };
  };
  const stage = (id, changes) => stageMany([{ id, changes }]);
  const remove = (ids) => {
    const next = { ...drafts };
    ids.forEach(id => delete next[id]);
    save(next);
  };
  const preview = useMemo(() => products.map(row => drafts[row.id]
    ? { ...row, ...drafts[row.id].changes, _stockDraft: true }
    : row), [products, drafts]);
  return { drafts, preview, stage, stageMany, remove, discard: () => save({}) };
}
